import { useState, useCallback, useLayoutEffect } from 'react';

const useCardPositions = (workflowRef, cardRefs, allCards, allCardsForCurrentTab, refsAreAttached, currentTab) => {
  const [cardPositions, setCardPositions] = useState({});

  const calculatePositions = useCallback(() => {
    const newPositions = {};
    if (!workflowRef.current) {
      return;
    }
    const workflowRect = workflowRef.current.getBoundingClientRect();

    allCards.forEach(card => {
      const cardId = card.id;
      const refObject = cardRefs[cardId];
      if (refObject && refObject.current) {
        const domNode = refObject.current;
        if (domNode.getBoundingClientRect) {
          const rect = domNode.getBoundingClientRect();
          newPositions[cardId] = {
            id: cardId,
            x: rect.left - workflowRect.left + rect.width / 2 + workflowRef.current.scrollLeft,
            y: rect.top - workflowRect.top + rect.height / 2 + workflowRef.current.scrollTop,
            width: rect.width,
            height: rect.height,
          };
        }
      }
    });

    // React will bail out of a re-render if newPositions is the same as current cardPositions
    setCardPositions(newPositions);
  }, [workflowRef, allCards, cardRefs]);

  useLayoutEffect(() => {
    if (!refsAreAttached) {
      return;
    }

    if (allCards.length === 0) {
      setCardPositions({}); // Clear positions if no cards
      return;
    }

    let allRefsAreCurrent = true;
    if (cardRefs && typeof cardRefs === 'object') {
      for (const card of allCards) {
        if (!cardRefs[card.id] || !cardRefs[card.id].current) {
          allRefsAreCurrent = false;
          break;
        }
      }
    } else {
      allRefsAreCurrent = false;
    }

    if (!allRefsAreCurrent) {
      return; // Exit if not all refs are ready
    }

    calculatePositions();

    const resizeObserver = new ResizeObserver(() => {
      calculatePositions();
    });

    // For ResizeObserver, we only need to observe cards in the current tab
    const currentCardDOMRefs = allCardsForCurrentTab
      .filter(card => cardRefs[card.id] && cardRefs[card.id].current)
      .map(card => cardRefs[card.id].current);
    
    currentCardDOMRefs.forEach(el => resizeObserver.observe(el));

    return () => {
      currentCardDOMRefs.forEach(el => resizeObserver.unobserve(el));
    };
  }, [refsAreAttached, allCards, allCardsForCurrentTab, workflowRef, calculatePositions, currentTab, cardRefs]);

  return cardPositions;
};

export default useCardPositions;
